import { create } from 'zustand'
import type { ChatMessage, ChatSession, SessionSettings } from '@/types/chat'

interface ChatWorkspaceState {
  sessions: ChatSession[]
  activeSessionId: string | null
  compareMode: boolean
  compareSelection: string[]
  settingsOpen: boolean
  settingsSheetOpen: boolean
  settingsCollapsed: boolean
  draftPrompt: string
  setSessions: (sessions: ChatSession[]) => void
  upsertSession: (session: ChatSession) => void
  removeOpenTab: (sessionId: string) => void
  setActiveSessionId: (sessionId: string | null) => void
  setCompareMode: (enabled: boolean) => void
  toggleCompareSelection: (sessionId: string) => void
  clearCompareSelection: () => void
  setSettingsOpen: (open: boolean) => void
  setSettingsSheetOpen: (open: boolean) => void
  setSettingsCollapsed: (collapsed: boolean) => void
  setDraftPrompt: (value: string) => void
  patchSessionMessage: (
    sessionId: string,
    messageId: string,
    patch: (message: ChatMessage) => ChatMessage,
  ) => void
  addSessionMessage: (sessionId: string, message: ChatMessage) => void
  updateLocalSettings: (sessionId: string, patch: Partial<SessionSettings>) => void
  updateLocalSystemPrompt: (sessionId: string, systemPrompt: string) => void
}

export const useChatWorkspaceStore = create<ChatWorkspaceState>((set, get) => ({
  sessions: [],
  activeSessionId: null,
  compareMode: false,
  compareSelection: [],
  settingsOpen: true,
  settingsSheetOpen: false,
  settingsCollapsed: true,
  draftPrompt: '',
  setSessions: (sessions) => {
    const openSessions = sessions.filter((session) => session.status === 'open')
    set({
      sessions: openSessions,
      activeSessionId: get().activeSessionId ?? openSessions[0]?.id ?? null,
    })
  },
  upsertSession: (session) =>
    set((state) => {
      const exists = state.sessions.some((item) => item.id === session.id)
      const sessions = exists
        ? state.sessions.map((item) => (item.id === session.id ? session : item))
        : [...state.sessions, session]

      return { sessions, activeSessionId: session.id }
    }),
  removeOpenTab: (sessionId) =>
    set((state) => {
      const sessions = state.sessions.filter((session) => session.id !== sessionId)
      const activeSessionId =
        state.activeSessionId === sessionId ? (sessions[0]?.id ?? null) : state.activeSessionId

      return { sessions, activeSessionId }
    }),
  setActiveSessionId: (sessionId) => set({ activeSessionId: sessionId }),
  setCompareMode: (enabled) =>
    set({
      compareMode: enabled,
      compareSelection: enabled ? [] : [],
    }),
  toggleCompareSelection: (sessionId) =>
    set((state) => {
      const selected = state.compareSelection.includes(sessionId)
        ? state.compareSelection.filter((id) => id !== sessionId)
        : [...state.compareSelection, sessionId]
      return { compareSelection: selected }
    }),
  clearCompareSelection: () => set({ compareSelection: [] }),
  setSettingsOpen: (open) => set({ settingsOpen: open }),
  setSettingsSheetOpen: (open) => set({ settingsSheetOpen: open }),
  setSettingsCollapsed: (collapsed) => set({ settingsCollapsed: collapsed }),
  setDraftPrompt: (value) => set({ draftPrompt: value }),
  patchSessionMessage: (sessionId, messageId, patch) =>
    set((state) => ({
      sessions: state.sessions.map((session) => {
        if (session.id !== sessionId) return session
        return {
          ...session,
          messages: session.messages.map((message) =>
            message.id === messageId ? patch(message) : message,
          ),
        }
      }),
    })),
  addSessionMessage: (sessionId, message) =>
    set((state) => ({
      sessions: state.sessions.map((session) =>
        session.id === sessionId
          ? {
              ...session,
              messages: [...session.messages, message],
              lastMessageAt: message.createdAt,
            }
          : session,
      ),
    })),
  updateLocalSettings: (sessionId, patch) =>
    set((state) => ({
      sessions: state.sessions.map((session) =>
        session.id === sessionId
          ? { ...session, defaultSettings: { ...session.defaultSettings, ...patch } }
          : session,
      ),
    })),
  updateLocalSystemPrompt: (sessionId, systemPrompt) =>
    set((state) => ({
      sessions: state.sessions.map((session) =>
        session.id === sessionId ? { ...session, systemPrompt } : session,
      ),
    })),
}))
